import { useState } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import { DocumentCheckIcon as DocumentCheckIconOutline } from '@heroicons/react/24/outline';
import { DocumentCheckIcon as DocumentCheckIconSolid } from '@heroicons/react/24/solid';
import { ExpandAllIcon } from '../../components_misc/ExpandAllIcon';
import { CollapseAllIcon } from '../../components_misc/CollapseAllIcon';
import type { PlanColumnNodeData } from '../types';

/** The column control cluster's rendered box (ADR B6; widened by
 * ADR.ImplementationPlansUX2 C4c): three `p-1` icon buttons (`h-4 w-4` glyph
 * + 4px padding on every side = 24px) and two `gap-1` (4px) gaps between
 * them — `3×24 + 2×4 = 80`, up from `2×24 + 4 = 52` before the hide-done
 * toggle. Used only to size the header's left spacer so the grid layout
 * below is symmetric and the `<h2>` stays optically centred — it is not part
 * of the card height budget (A1c) and pins no `planLayout` constant, because
 * the column header's own height is unchanged by this round. */
const COLUMN_CONTROL_SIZE = 24;
const COLUMN_CONTROL_GAP = 4;
const COLUMN_CONTROL_COUNT = 3;
const COLUMN_CONTROL_CLUSTER_WIDTH =
  COLUMN_CONTROL_COUNT * COLUMN_CONTROL_SIZE + (COLUMN_CONTROL_COUNT - 1) * COLUMN_CONTROL_GAP;

/**
 * PlanColumnNode — static background node for one board column (ADR §2).
 *
 * Columns are ReactFlow nodes (draggable: false, selectable: false), not DOM
 * overlays: flow coordinates equal screen coordinates with zoom pinned to 1, so
 * cards and columns share one coordinate system and the drop math stays trivial.
 * Dimensions come from planLayout via node data — never measured.
 *
 * **The header's Expand-all / Collapse-all pair (ADR B6/B6a).** Two `nodrag`
 * icon buttons at the header's right end, `ExpandAllIcon` then
 * `CollapseAllIcon` (treeview's bulk-action order — the opposite of the
 * card's own Collapse-then-Expand pair, which copies `MapTitleBar` instead;
 * each order is faithful to its own named precedent, ADR B6 Consequences).
 * Neither is ever `disabled` — an empty column's buttons are silent no-ops,
 * the same reasoning B4's card pair uses to avoid `MapTitleBar`'s WCAG 2.4.3
 * focus-loss defect. Neither carries `aria-expanded` or `aria-pressed`: these
 * are bulk actions with no single state to report, and a column can be
 * partly collapsed, so a pressed state that is sometimes true is worse than
 * none.
 *
 * The header becomes `MapTitleBar.tsx:75`'s
 * `grid grid-cols-[auto_1fr_auto]` layout, with an inert left spacer sized to
 * the control cluster (`COLUMN_CONTROL_CLUSTER_WIDTH`) so the `<h2>` — inside
 * the centre `1fr` track — stays optically centred where it was under the
 * shipped `flex justify-center` header.
 *
 * **The `sr-only` `aria-live="polite"` live region** (ADR B6) announces
 * `Expanded ${cardCount} cards in ${label}.` / `Collapsed ${cardCount} cards
 * in ${label}.` on every activation, counted in both directions — treeview's
 * `announceCustom` (`TreeViewContainer.tsx:341,346`) rendered in mm-web's
 * inline-`aria-live` idiom (`DESIGN.md`), never a Toast. `cardCount` comes
 * from node data, filled by the canvas from `layout.visibleColumnCards[id].length`
 * (ADR.ImplementationPlansUX2 C5e) — the exact set the click is about to act
 * on, read synchronously before the click mutates anything (both buttons act
 * on every VISIBLE card in the column, so the count does not depend on which
 * cards were already collapsed/expanded, and never counts a card the filter
 * is currently hiding).
 *
 * **The third button: hide-done (ADR.ImplementationPlansUX2 C4).** Placed
 * *after* the pair above, in the SAME `gap-1` cluster, with **no divider** —
 * per-column scope (C4a) means all three controls are equally scoped to this
 * column, so there is no boundary to mark. `DocumentCheckIcon`, **solid**
 * when this column's `hideDone === false` (showing all) and **outline** when
 * `true` (hiding done) — Bryan's icon polarity, asserted by unit 40.
 * `aria-pressed={hideDone}` — the one control here that DOES carry it,
 * because unlike its two neighbours (bulk actions with no single state to
 * report) this is a genuine two-state toggle and per-column scope keeps
 * every instance reporting only its own state (C4c). The accessible name is
 * **static-shape**: it never changes with pressed-ness (APG) — the count is
 * data the name states, exactly like `cardCount` above; `title` carries the
 * next-action hint instead. Never `disabled`, including at `doneCount === 0`
 * (B4/B6's WCAG 2.4.3 reasoning). `data-control="toggle-hide-done"` +
 * `data-column={columnId}` — the two-attribute selector C6b's focus fallback
 * resolves; `data-column` is what makes the pair unique across the three
 * rendered headers (unit 41).
 *
 * The live region above narrates the hide-done activation too (C4d):
 * `Hiding done objectives in ${label}. ${doneCount} hidden.` when it turns
 * on, `Showing all objectives in ${label}.` when it turns off — read from
 * `doneCount`, the FULL column order (C5e), not the visible one, so the
 * announcement never says "0 hidden" at the moment cards disappear.
 */
export function PlanColumnNode({ data }: NodeProps<Node<PlanColumnNodeData>>) {
  const {
    columnId,
    label,
    width,
    height,
    cardCount,
    onExpandAll,
    onCollapseAll,
    hideDone,
    doneCount,
    onToggleHideDone,
  } = data;
  const [announcement, setAnnouncement] = useState('');

  const handleExpandAll = () => {
    onExpandAll?.(columnId);
    setAnnouncement(`Expanded ${cardCount} cards in ${label}.`);
  };
  const handleCollapseAll = () => {
    onCollapseAll?.(columnId);
    setAnnouncement(`Collapsed ${cardCount} cards in ${label}.`);
  };
  const handleToggleHideDone = () => {
    onToggleHideDone?.(columnId);
    setAnnouncement(
      hideDone
        ? `Showing all objectives in ${label}.`
        : `Hiding done objectives in ${label}. ${doneCount} hidden.`,
    );
  };
  const DocumentCheckIcon = hideDone ? DocumentCheckIconOutline : DocumentCheckIconSolid;

  return (
    <div
      role="group"
      aria-label={`${label} column`}
      className="rounded-md border border-zinc-200 bg-zinc-50"
      style={{ width, height }}
    >
      <div className="grid h-12 grid-cols-[auto_1fr_auto] items-center border-b border-zinc-200 px-2">
        {/* Inert left spacer, sized to the control cluster so the centre
            track — and the <h2> inside it — stays optically centred
            (MapTitleBar.tsx:75's layout, applied here). */}
        <div aria-hidden="true" style={{ width: COLUMN_CONTROL_CLUSTER_WIDTH }} />
        <h2 className="truncate text-center text-sm font-semibold text-zinc-700">{label}</h2>
        {/* `pointer-events-auto` is load-bearing, not decoration. ReactFlow's
            NodeWrapper sets `pointerEvents: hasPointerEvents ? 'all' : 'none'`
            on the node's own wrapper div, where `hasPointerEvents = isSelectable
            || isDraggable || onClick || onMouseEnter || onMouseMove ||
            onMouseLeave` (`@xyflow/react@12.7.1`). The column node sets
            `selectable: false, draggable: false` and the canvas registers no
            board-level onNodeClick/onNodeMouseEnter/etc, so `hasPointerEvents`
            is false and the wrapper is `pointer-events: none` — harmless
            before this round, when the column had no interactive children,
            and silently unclickable now that it does. `pointer-events: auto`
            on a descendant overrides an ancestor's `none`; it does not need to
            travel all the way to the node root, only to the first ancestor of
            the actually-interactive elements. */}
        <div className="pointer-events-auto flex items-center gap-1">
          <button
            type="button"
            className="nodrag rounded p-1 text-zinc-600 hover:bg-zinc-200"
            aria-label={`Expand all cards in ${label}`}
            title="Expand all"
            onClick={handleExpandAll}
          >
            <ExpandAllIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="nodrag rounded p-1 text-zinc-600 hover:bg-zinc-200"
            aria-label={`Collapse all cards in ${label}`}
            title="Collapse all"
            onClick={handleCollapseAll}
          >
            <CollapseAllIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          {/* Hide-done (ADR.ImplementationPlansUX2 C4a-C4d): no divider —
              per-column scope leaves no boundary to mark. Never `disabled`
              (B4/B6's WCAG 2.4.3 reasoning), including at `doneCount === 0`. */}
          <button
            type="button"
            className="nodrag rounded p-1 text-zinc-600 hover:bg-zinc-200"
            aria-pressed={hideDone}
            aria-label={`Hide done objectives in ${label} (${doneCount} done)`}
            title={hideDone ? 'Show done' : 'Hide done'}
            data-control="toggle-hide-done"
            data-column={columnId}
            onClick={handleToggleHideDone}
          >
            <DocumentCheckIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* Counted, both directions (ADR B6): always present in the DOM so a
          screen reader is subscribed before the first activation — the same
          "always mounted" shape the eyebrow's "Copied!" span uses. */}
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
